import { useEffect, useMemo, useState } from "react";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Plus, PencilSimple, Trash, MagnifyingGlass, UploadSimple, Download } from "@phosphor-icons/react";
import ImportModal from "@/components/ImportModal";
import ColumnFilter, { applyColumnFilters, dynamicUniques, SortableHeader, sortRows } from "@/components/ColumnFilter";
import ScanButton from "@/components/ScanButton";
import { useAuth } from "@/context/AuthContext";

const EMPTY = { sku: "", name: "", description: "", manufacturer_code: "", category_id: "", supplier_id: "", price: 0, min_stock: 0, is_serialized: true, unit_of_measure: "pz" };
const UOM_SUGGESTIONS = ["pz", "kg", "g", "m", "cm", "mm", "l", "ml", "mq", "mc", "cf", "sc", "rt", "kW", "h"];

export default function Products() {
  const { canWrite } = useAuth();
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [q, setQ] = useState("");
  const [importOpen, setImportOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/products", { params: q ? { q } : {} });
      setRows(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const loadRefs = async () => {
    const [c, s] = await Promise.all([api.get("/categories"), api.get("/suppliers")]);
    setCategories(c.data); setSuppliers(s.data);
  };

  useEffect(() => { load(); loadRefs(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  const openNew = () => { setEditing(null); setForm(EMPTY); setShowForm(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      sku: r.sku, name: r.name, description: r.description || "",
      manufacturer_code: r.manufacturer_code || "",
      category_id: r.category_id || "", supplier_id: r.supplier_id || "",
      price: r.price || 0, min_stock: r.min_stock || 0,
      is_serialized: r.is_serialized !== false,
      unit_of_measure: r.unit_of_measure || "pz",
    });
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form, price: parseFloat(form.price) || 0, min_stock: parseInt(form.min_stock) || 0,
                      category_id: form.category_id || null, supplier_id: form.supplier_id || null,
                      is_serialized: Boolean(form.is_serialized),
                      unit_of_measure: (form.unit_of_measure || "pz").trim() || "pz" };
    try {
      if (editing) {
        await api.put(`/products/${editing.id}`, payload);
        toast.success("Prodotto aggiornato");
      } else {
        await api.post("/products", payload);
        toast.success("Prodotto creato");
      }
      setShowForm(false); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Eliminare "${r.name}"?`)) return;
    try {
      await api.delete(`/products/${r.id}`);
      toast.success("Prodotto eliminato");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const catName = (id) => categories.find((c) => c.id === id)?.name || "—";
  const supName = (id) => suppliers.find((s) => s.id === id)?.name || "—";

  const [filters, setFilters] = useState({
    sku: new Set(), manufacturer_code: new Set(), name: new Set(),
    tracking: new Set(), category: new Set(), supplier: new Set(),
    stock: new Set(), unit_of_measure: new Set(), price: new Set(),
  });
  const [sortCfg, setSortCfg] = useState(null);
  const setFilter = (key) => (v) => setFilters((f) => ({ ...f, [key]: v }));

  const accessors = useMemo(() => ({
    sku: (r) => r.sku || "",
    manufacturer_code: (r) => r.manufacturer_code || "",
    name: (r) => r.name || "",
    tracking: (r) => (r.is_serialized === false ? "Quantità" : "Seriale"),
    category: (r) => catName(r.category_id),
    supplier: (r) => supName(r.supplier_id),
    stock: (r) => r.stock || 0,
    unit_of_measure: (r) => r.unit_of_measure || "pz",
    price: (r) => r.price || 0,
  }), [categories, suppliers]);

  const filteredRows = useMemo(
    () => sortRows(applyColumnFilters(rows, filters, accessors), sortCfg, accessors),
    [rows, filters, accessors, sortCfg]
  );

  const uniques = useMemo(
    () => dynamicUniques(rows, filters, accessors),
    [rows, filters, accessors]
  );

  const downloadXlsx = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE}/export/products`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "prodotti.xlsx"; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Errore export"); }
  };

  return (
    <div>
      <PageHeader
        title="Prodotti"
        subtitle="Anagrafica prodotti con SKU univoco"
        action={
          <div className="flex gap-2">
            <button data-testid="export-products-btn" onClick={downloadXlsx} className="text-sm px-4 py-2.5 border border-neutral-300 bg-white hover:bg-neutral-50 transition-colors flex items-center gap-2">
              <Download size={16} /> <span className="hidden sm:inline">Export XLSX</span>
            </button>
            {canWrite && (
              <button data-testid="import-products-btn" onClick={() => setImportOpen(true)} className="text-sm px-4 py-2.5 border border-neutral-300 bg-white hover:bg-neutral-50 transition-colors flex items-center gap-2">
                <UploadSimple size={16} /> <span className="hidden sm:inline">Import XLSX</span>
              </button>
            )}
            {canWrite && (
              <button data-testid="new-product-btn" onClick={openNew} className="text-sm px-4 py-2.5 bg-[#002FA7] hover:bg-[#002480] text-white transition-colors duration-150 flex items-center gap-2">
                <Plus size={16} weight="bold" /> <span className="hidden sm:inline">Nuovo Prodotto</span><span className="sm:hidden">Nuovo</span>
              </button>
            )}
          </div>
        }
      />
      <div className="p-8 space-y-4">
        <div className="flex items-center gap-2 bg-white border border-neutral-200 px-3 py-2 max-w-md">
          <MagnifyingGlass size={16} className="text-neutral-400" />
          <input data-testid="products-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per codice interno, codice produttore o nome..." className="flex-1 outline-none text-sm bg-transparent" />
        </div>

        <div className="bg-white border border-neutral-200 overflow-auto max-h-[calc(100vh-260px)]">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 border-b border-neutral-200 sticky top-0 z-20 shadow-sm">
              <tr>
                <th className="px-4 py-3 label-eyebrow text-center bg-neutral-50">
                  <span className="inline-flex items-center gap-1 justify-center w-full">
                    <SortableHeader testid="sort-p-sku" label="Codice Interno" sortKey="sku" sortConfig={sortCfg} setSortConfig={setSortCfg} />
                    <ColumnFilter testid="filter-p-sku" values={uniques.sku} selected={filters.sku} onChange={setFilter("sku")} />
                  </span>
                </th>
                <th className="px-4 py-3 label-eyebrow text-center bg-neutral-50">
                  <span className="inline-flex items-center gap-1 justify-center w-full">
                    <SortableHeader testid="sort-p-mcode" label="Cod. Produttore" sortKey="manufacturer_code" sortConfig={sortCfg} setSortConfig={setSortCfg} />
                    <ColumnFilter testid="filter-p-mcode" values={uniques.manufacturer_code} selected={filters.manufacturer_code} onChange={setFilter("manufacturer_code")} />
                  </span>
                </th>
                <th className="px-4 py-3 label-eyebrow text-center bg-neutral-50">
                  <span className="inline-flex items-center gap-1 justify-center w-full">
                    <SortableHeader testid="sort-p-name" label="Nome" sortKey="name" sortConfig={sortCfg} setSortConfig={setSortCfg} />
                    <ColumnFilter testid="filter-p-name" values={uniques.name} selected={filters.name} onChange={setFilter("name")} />
                  </span>
                </th>
                <th className="px-4 py-3 label-eyebrow text-center bg-neutral-50">
                  <span className="inline-flex items-center gap-1 justify-center w-full">
                    <SortableHeader testid="sort-p-tracking" label="Tracciam." sortKey="tracking" sortConfig={sortCfg} setSortConfig={setSortCfg} />
                    <ColumnFilter testid="filter-p-tracking" values={uniques.tracking} selected={filters.tracking} onChange={setFilter("tracking")} />
                  </span>
                </th>
                <th className="px-4 py-3 label-eyebrow text-center bg-neutral-50">
                  <span className="inline-flex items-center gap-1 justify-center w-full">
                    <SortableHeader testid="sort-p-category" label="Categoria" sortKey="category" sortConfig={sortCfg} setSortConfig={setSortCfg} />
                    <ColumnFilter testid="filter-p-category" values={uniques.category} selected={filters.category} onChange={setFilter("category")} />
                  </span>
                </th>
                <th className="px-4 py-3 label-eyebrow text-center bg-neutral-50">
                  <span className="inline-flex items-center gap-1 justify-center w-full">
                    <SortableHeader testid="sort-p-supplier" label="Fornitore" sortKey="supplier" sortConfig={sortCfg} setSortConfig={setSortCfg} />
                    <ColumnFilter testid="filter-p-supplier" values={uniques.supplier} selected={filters.supplier} onChange={setFilter("supplier")} />
                  </span>
                </th>
                <th className="px-4 py-3 label-eyebrow text-center bg-neutral-50">
                  <span className="inline-flex items-center gap-1 justify-center w-full">
                    <SortableHeader testid="sort-p-stock" label="Giacenza" sortKey="stock" sortConfig={sortCfg} setSortConfig={setSortCfg} />
                    <ColumnFilter testid="filter-p-stock" values={uniques.stock} selected={filters.stock} onChange={setFilter("stock")} />
                  </span>
                </th>
                <th className="px-4 py-3 label-eyebrow text-center bg-neutral-50">
                  <span className="inline-flex items-center gap-1 justify-center w-full">
                    <SortableHeader testid="sort-p-uom" label="U.M." sortKey="unit_of_measure" sortConfig={sortCfg} setSortConfig={setSortCfg} />
                    <ColumnFilter testid="filter-p-uom" values={uniques.unit_of_measure} selected={filters.unit_of_measure} onChange={setFilter("unit_of_measure")} />
                  </span>
                </th>
                <th className="px-4 py-3 label-eyebrow text-center bg-neutral-50">
                  <span className="inline-flex items-center gap-1 justify-center w-full">
                    <SortableHeader testid="sort-p-price" label="Prezzo" sortKey="price" sortConfig={sortCfg} setSortConfig={setSortCfg} />
                    <ColumnFilter testid="filter-p-price" values={uniques.price} selected={filters.price} onChange={setFilter("price")} />
                  </span>
                </th>
                <th className="px-4 py-3 label-eyebrow text-center bg-neutral-50">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.length === 0 ? (
                <tr><td colSpan="10" className="px-4 py-12 text-center text-neutral-500">Nessun prodotto.</td></tr>
              ) : filteredRows.map((r) => (
                <tr key={r.id} className="border-b border-neutral-100 hover:bg-neutral-50 transition-colors">
                  <td className="px-4 py-3 font-mono text-xs font-semibold">{r.sku}</td>
                  <td className="px-4 py-3 font-mono text-xs text-neutral-600">{r.manufacturer_code || "—"}</td>
                  <td className="px-4 py-3 font-medium">{r.name}</td>
                  <td className="px-4 py-3">
                    {r.is_serialized === false ? (
                      <span className="inline-flex px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-200">Quantità</span>
                    ) : (
                      <span className="inline-flex px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider bg-[#002FA7]/10 text-[#002FA7] border border-[#002FA7]/20">Seriale</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-neutral-600">{catName(r.category_id)}</td>
                  <td className="px-4 py-3 text-neutral-600">{supName(r.supplier_id)}</td>
                  <td className={`px-4 py-3 text-right font-mono ${r.stock <= (r.min_stock || 0) && r.stock >= 0 ? "text-red-600 font-semibold" : ""}`}>{r.stock}</td>
                  <td className="px-4 py-3 text-center font-mono text-xs text-neutral-600">{r.unit_of_measure || "pz"}</td>
                  <td className="px-4 py-3 text-right font-mono">€ {Number(r.price || 0).toFixed(2)}</td>
                  <td className="px-4 py-3 text-right">
                    {canWrite && <button data-testid={`edit-product-${r.id}`} onClick={() => openEdit(r)} className="p-1.5 hover:bg-neutral-200 transition-colors mr-1"><PencilSimple size={16} /></button>}
                    {canWrite && <button data-testid={`delete-product-${r.id}`} onClick={() => remove(r)} className="p-1.5 hover:bg-red-100 text-red-600 transition-colors"><Trash size={16} /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-2 md:p-4">
          <form onSubmit={submit} className="bg-white border border-neutral-200 shadow-xl w-full max-w-2xl max-h-[95vh] flex flex-col" data-testid="product-form">
            <div className="p-4 md:p-6 border-b border-neutral-200 shrink-0">
              <div className="label-eyebrow mb-1">{editing ? "Modifica" : "Nuovo"}</div>
              <h2 className="font-display font-bold text-xl tracking-tight">Prodotto</h2>
            </div>
            <div className="p-4 md:p-6 grid grid-cols-1 md:grid-cols-2 gap-4 overflow-y-auto flex-1">
              <label className="block">
                <span className="label-eyebrow block mb-2">SKU *</span>
                <div className="flex items-stretch gap-0">
                  <input required data-testid="product-sku" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} className="flex-1 px-3 py-2 border border-neutral-300 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
                  <ScanButton testid="product-sku-scan" title="Scansiona SKU" scannerTitle="Scanner SKU" onScan={(code) => setForm({ ...form, sku: code })} className="border-l-0" />
                </div>
              </label>
              <label className="block">
                <span className="label-eyebrow block mb-2">Codice Produttore</span>
                <div className="flex items-stretch gap-0">
                  <input data-testid="product-manufacturer-code" value={form.manufacturer_code} onChange={(e) => setForm({ ...form, manufacturer_code: e.target.value })} className="flex-1 px-3 py-2 border border-neutral-300 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
                  <ScanButton testid="product-mcode-scan" title="Scansiona Codice Produttore" scannerTitle="Scanner Codice Produttore" onScan={(code) => setForm({ ...form, manufacturer_code: code })} className="border-l-0" />
                </div>
              </label>
              <label className="block">
                <span className="label-eyebrow block mb-2">Nome *</span>
                <input required data-testid="product-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2 border border-neutral-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
              </label>
              <label className="block">
                <span className="label-eyebrow block mb-2">Categoria</span>
                <select data-testid="product-category" value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })} className="w-full px-3 py-2 border border-neutral-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
                  <option value="">— Nessuna —</option>
                  {categories.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
                </select>
              </label>
              <label className="block">
                <span className="label-eyebrow block mb-2">Fornitore</span>
                <select data-testid="product-supplier" value={form.supplier_id} onChange={(e) => setForm({ ...form, supplier_id: e.target.value })} className="w-full px-3 py-2 border border-neutral-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
                  <option value="">— Nessuno —</option>
                  {suppliers.map((s) => (<option key={s.id} value={s.id}>{s.name}</option>))}
                </select>
              </label>
              <label className="block">
                <span className="label-eyebrow block mb-2">Prezzo Unit. (€)</span>
                <input type="number" step="0.01" min="0" data-testid="product-price" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="w-full px-3 py-2 border border-neutral-300 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
              </label>
              <label className="block">
                <span className="label-eyebrow block mb-2">Scorta Minima</span>
                <input type="number" min="0" data-testid="product-min-stock" value={form.min_stock} onChange={(e) => setForm({ ...form, min_stock: e.target.value })} className="w-full px-3 py-2 border border-neutral-300 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
              </label>
              <label className="block">
                <span className="label-eyebrow block mb-2">Unità di misura</span>
                <input
                  type="text"
                  list="uom-suggestions"
                  data-testid="product-unit-of-measure"
                  value={form.unit_of_measure}
                  onChange={(e) => setForm({ ...form, unit_of_measure: e.target.value })}
                  placeholder="es. pz, kg, m, l, mq, cf"
                  className="w-full px-3 py-2 border border-neutral-300 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                />
                <datalist id="uom-suggestions">
                  {UOM_SUGGESTIONS.map((u) => (<option key={u} value={u} />))}
                </datalist>
              </label>
              <label className="block md:col-span-2">
                <span className="label-eyebrow block mb-2">Descrizione</span>
                <textarea data-testid="product-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="w-full px-3 py-2 border border-neutral-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
              </label>
              <label className="md:col-span-2 flex items-start gap-3 p-3 border border-neutral-200 bg-neutral-50 cursor-pointer hover:bg-neutral-100 transition-colors">
                <input
                  type="checkbox"
                  data-testid="product-is-serialized"
                  checked={Boolean(form.is_serialized)}
                  onChange={(e) => setForm({ ...form, is_serialized: e.target.checked })}
                  className="mt-0.5 w-4 h-4 accent-[#002FA7]"
                />
                <div className="flex-1">
                  <div className="text-sm font-semibold">Tracciato per numero seriale univoco</div>
                  <div className="text-xs text-neutral-600 mt-0.5">
                    Attivo per apparati con seriale (router, decoder, ecc.). Disattiva per materiali sfusi (cavi, connettori, viti) che vengono tracciati solo per quantità.
                  </div>
                </div>
              </label>
            </div>
            <div className="p-4 md:p-6 pt-3 flex justify-end gap-2 border-t border-neutral-200 shrink-0">
              <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-sm border border-neutral-300 hover:bg-neutral-100 transition-colors">Annulla</button>
              <button data-testid="product-submit" type="submit" className="px-4 py-2 text-sm bg-[#002FA7] hover:bg-[#002480] text-white transition-colors">{editing ? "Salva" : "Crea"}</button>
            </div>
          </form>
        </div>
      )}

      <ImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        endpoint="/products/import"
        templateEndpoint="/import/template/products"
        title="Prodotti"
        testidPrefix="products-import"
        onSuccess={load}
      />
    </div>
  );
}

